import { useState, useEffect, useRef } from 'react';

interface UseScrollControllerOptions {
  /**
   * Minimum scroll distance in pixels required to trigger show/hide
   * @default 100
   */
  threshold?: number;
  /**
   * When true, scroll events will not hide the header. Used to keep the header
   * (and any open dropdown menu inside it) visible while the user is interacting
   * with it — e.g. hovering the Neighborhoods/Buildings popup and scrolling.
   * @default false
   */
  paused?: boolean;
}

interface UseScrollControllerReturn {
  /**
   * Whether the header should be shown
   */
  shouldShow: boolean;
  /**
   * Current scroll position
   */
  scrollY: number;
  /**
   * Whether user is scrolling down
   */
  isScrollingDown: boolean;
}

/**
 * Custom hook to track scroll direction and determine when to show/hide header
 *
 * @param options - Configuration options
 * @returns Object containing shouldShow state and scroll information
 *
 * @example
 * ```tsx
 * const { shouldShow, scrollY } = useScrollController({ threshold: 100 });
 * ```
 */
export function useScrollController(
  options: UseScrollControllerOptions = {}
): UseScrollControllerReturn {
  const { threshold = 100, paused = false } = options;

  const lastScrollY = useRef(0);
  const [shouldShow, setShouldShow] = useState(true);
  const [isScrollingDown, setIsScrollingDown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // While paused (e.g. user is hovering an open header dropdown), keep the
      // header visible and just track the scroll position so we don't jump when
      // resuming.
      if (paused) {
        lastScrollY.current = currentScrollY;
        return;
      }

      const scrollingDown = currentScrollY > lastScrollY.current;

      setIsScrollingDown(scrollingDown);

      if (scrollingDown && currentScrollY > threshold) {
        setShouldShow(false);
      } else if (!scrollingDown) {
        const scrollDifference = lastScrollY.current - currentScrollY;
        if (scrollDifference >= threshold || currentScrollY < threshold) {
          setShouldShow(true);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold, paused]);

  return {
    shouldShow,
    scrollY: lastScrollY.current,
    isScrollingDown,
  };
}
